<section class="d-flex flex-column gap-2" data-testid="invoice-detail-page">
  <a
    [routerLink]="documentsLink()"
    class="back-link av-meta d-inline-flex align-items-center gap-1"
    data-testid="invoice-detail-back"
  >
    <mat-icon aria-hidden="true">arrow_back</mat-icon>
    <span>{{ 'backoffice.billing.invoice_detail.back' | translate }}</span>
  </a>

  <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
    <div class="d-flex flex-column gap-1">
      <h2 class="av-section-title m-0">{{ 'backoffice.billing.invoice_detail.title' | translate }}</h2>

      @if (facade.organizationName()) {
        <p class="cell-strong m-0" data-testid="invoice-detail-organization">
          {{ facade.organizationName() }}
        </p>
      }

      <p class="av-meta m-0">{{ 'backoffice.billing.invoices.advance_note' | translate }}</p>
    </div>

    <button type="button" class="av-btn" (click)="reload()" data-testid="invoice-detail-reload">
      <mat-icon aria-hidden="true">refresh</mat-icon>
      <span>{{ 'backoffice.common.reload' | translate }}</span>
    </button>
  </div>
</section>

@switch (facade.state()) {
  @case ('error') {
    <av-empty-state
      class="mt-3"
      icon="error_outline"
      [title]="'backoffice.billing.invoice_detail.empty_title' | translate"
      [message]="'backoffice.billing.invoice_detail.empty_message' | translate"
    ></av-empty-state>
  }
  @case ('ready') {
    @if (facade.invoice(); as invoice) {
      <section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="invoice-summary">
        <div class="d-flex flex-wrap gap-4">
          <div class="d-flex flex-column gap-1">
            <span class="av-meta">{{ 'backoffice.billing.invoice_detail.invoice' | translate }}</span>
            <span class="av-source" data-testid="invoice-stripe-id">
              {{ invoice.stripe_invoice_id || unresolved }}
            </span>
          </div>

          <div class="d-flex flex-column gap-1">
            <span class="av-meta">{{ 'backoffice.billing.invoice_detail.status' | translate }}</span>
            <av-status-chip
              [label]="invoice.statusLabelKey | translate"
              [tone]="invoice.statusTone"
            ></av-status-chip>
          </div>

          <div class="d-flex flex-column gap-1">
            <span class="av-meta">{{ 'backoffice.billing.invoice_detail.plan' | translate }}</span>
            <span class="cell-strong">{{ invoice.plan_name || unresolved }}</span>
          </div>

          <div class="d-flex flex-column gap-1">
            <span class="av-meta">{{ 'backoffice.billing.invoice_detail.period' | translate }}</span>
            <span class="cell-strong" data-testid="invoice-period">
              {{ invoice.period_start ? (invoice.period_start | date: dateFormat) : unresolved }}
              —
              {{ invoice.period_end ? (invoice.period_end | date: dateFormat) : unresolved }}
            </span>
          </div>

          <div class="d-flex flex-column gap-1">
            <span class="av-meta">{{ 'backoffice.billing.invoice_detail.issued' | translate }}</span>
            <span class="cell-strong">
              {{ invoice.issued_at ? (invoice.issued_at | date: dateTimeFormat) : unresolved }}
            </span>
          </div>

          <div class="d-flex flex-column gap-1">
            <span class="av-meta">{{ 'backoffice.billing.invoice_detail.collection' | translate }}</span>
            <span class="cell-strong">{{ invoice.collection_method || unresolved }}</span>
          </div>
        </div>

        <div class="d-flex flex-wrap gap-4">
          <div class="d-flex flex-column gap-1">
            <span class="av-meta">{{ 'backoffice.billing.invoice_detail.seats' | translate }}</span>
            <span class="cell-strong" data-testid="invoice-detail-seat-quantity">
              {{ invoice.seat_quantity ?? unresolved }}
            </span>
          </div>

          <div class="d-flex flex-column gap-1">
            <span class="av-meta">{{ 'backoffice.billing.invoice_detail.devices' | translate }}</span>
            <span class="cell-strong" data-testid="invoice-detail-device-quantity">
              {{ invoice.device_quantity ?? unresolved }}
            </span>
          </div>

          <div class="d-flex flex-column gap-1">
            <span class="av-meta">{{ 'backoffice.billing.invoice_detail.subtotal' | translate }}</span>
            <span class="cell-strong">
              {{ invoice.subtotal === null ? unresolved : (invoice.subtotal | number: '1.2-2') }}
              {{ invoice.currency || '' }}
            </span>
          </div>

          <div class="d-flex flex-column gap-1">
            <span class="av-meta">{{ 'backoffice.billing.invoice_detail.tax' | translate }}</span>
            <span class="cell-strong">
              {{ invoice.tax === null ? unresolved : (invoice.tax | number: '1.2-2') }}
            </span>
          </div>

          <div class="d-flex flex-column gap-1">
            <span class="av-meta">{{ 'backoffice.billing.invoice_detail.total' | translate }}</span>
            <span class="cell-strong" data-testid="invoice-total">
              {{ invoice.total === null ? unresolved : (invoice.total | number: '1.2-2') }}
              {{ invoice.currency || '' }}
            </span>
          </div>

          <div class="d-flex flex-column gap-1">
            <span class="av-meta">{{ 'backoffice.billing.invoice_detail.creditable_remainder' | translate }}</span>
            <span class="cell-strong" data-testid="invoice-creditable-remainder">
              {{ invoice.creditable_remainder | number: '1.2-2' }} {{ invoice.currency || '' }}
            </span>
          </div>
        </div>

        <p class="av-meta m-0" data-testid="invoice-detail-snapshot-note">{{ 'backoffice.billing.invoices.snapshot_note' | translate }}</p>

        <div class="d-flex flex-wrap gap-2">
          <button
            type="button"
            class="av-btn"
            [disabled]="!invoice.hasDocument"
            [matTooltip]="
              invoice.hasDocument ? '' : ('backoffice.billing.invoice_actions.pdf_missing' | translate)
            "
            (click)="openDocument()"
            data-testid="invoice-detail-open-document"
          >
            <mat-icon aria-hidden="true">open_in_new</mat-icon>
            <span>{{ 'backoffice.billing.invoice_actions.pdf' | translate }}</span>
          </button>

          <button
            type="button"
            class="av-btn"
            [disabled]="!facade.canSend() || !invoice.isSendable"
            (click)="openResend()"
            data-testid="invoice-resend"
          >
            <mat-icon aria-hidden="true">forward_to_inbox</mat-icon>
            <span>{{ 'backoffice.billing.invoice_actions.resend' | translate }}</span>
          </button>

          <button
            type="button"
            class="av-btn av-btn-danger"
            [disabled]="!facade.canMoveMoney() || !invoice.isUncollectible"
            (click)="openUncollectible()"
            data-testid="invoice-uncollectible"
          >
            <mat-icon aria-hidden="true">money_off</mat-icon>
            <span>{{ 'backoffice.billing.invoice_actions.uncollectible' | translate }}</span>
          </button>

          <button
            type="button"
            class="av-btn"
            [disabled]="!facade.canMoveMoney() || !invoice.isCreditable"
            (click)="openCreditNote()"
            data-testid="invoice-credit-note"
          >
            <mat-icon aria-hidden="true">receipt</mat-icon>
            <span>{{ 'backoffice.billing.invoice_actions.credit_note' | translate }}</span>
          </button>
        </div>

        <p class="av-meta m-0" data-testid="invoice-cancellation-note">
          {{ 'backoffice.billing.refunds.not_from_cancellation' | translate }}
        </p>
      </section>

      <section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="invoice-lines">
        <div class="d-flex flex-column gap-1">
          <h3 class="av-section-title m-0">{{ 'backoffice.billing.invoice_detail.lines_title' | translate }}</h3>
          <p class="av-meta m-0">{{ 'backoffice.billing.invoice_detail.lines_note' | translate }}</p>
        </div>

        @if (invoice.lines.length === 0) {
          <p class="av-meta m-0" data-testid="invoice-lines-empty">{{ 'backoffice.billing.invoice_detail.lines_empty' | translate }}</p>
        } @else {
          <div class="table-scroll">
            <table class="av-table w-100" data-testid="invoice-line-table">
              <thead>
                <tr>
                  <th scope="col">{{ 'backoffice.billing.invoice_detail.lines_table.component' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.billing.invoice_detail.lines_table.quantity' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.billing.invoice_detail.lines_table.unit' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.billing.invoice_detail.credit_notes_table.amount' | translate }}</th>
                  <th scope="col">{{ 'backoffice.billing.invoice_detail.lines_table.period' | translate }}</th>
                </tr>
              </thead>

              <tbody>
                @for (line of invoice.lines; track line.id) {
                  <tr>
                    <td class="cell-strong">{{ componentLabelKey(line.component_type) | translate }}</td>
                    <td class="text-end">{{ line.quantity }}</td>
                    <td class="text-end">
                      {{ line.unit_amount === null ? unresolved : (line.unit_amount | number: '1.2-2') }}
                    </td>
                    <td class="text-end">
                      {{ line.amount === null ? unresolved : (line.amount | number: '1.2-2') }}
                      {{ line.currency || '' }}
                    </td>
                    <td class="cell-muted">
                      {{ line.period_start ? (line.period_start | date: dateFormat) : unresolved }}
                      —
                      {{ line.period_end ? (line.period_end | date: dateFormat) : unresolved }}
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </section>

      <section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="invoice-payments">
        <h3 class="av-section-title m-0">{{ 'backoffice.billing.invoice_detail.payments_title' | translate }}</h3>

        @if (invoice.payments.length === 0) {
          <p class="av-meta m-0" data-testid="invoice-payments-empty">{{ 'backoffice.billing.invoice_detail.payments_empty' | translate }}</p>
        } @else {
          <div class="table-scroll">
            <table class="av-table w-100" data-testid="invoice-payment-table">
              <thead>
                <tr>
                  <th scope="col">{{ 'backoffice.billing.invoice_detail.payments_table.payment' | translate }}</th>
                  <th scope="col">{{ 'backoffice.billing.invoice_detail.status' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.billing.invoice_detail.credit_notes_table.amount' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.billing.invoice_detail.payments_table.refunded' | translate }}</th>
                  <th scope="col">{{ 'backoffice.billing.invoice_detail.payments_table.failure' | translate }}</th>
                  <th scope="col">{{ 'backoffice.billing.invoice_detail.payments_table.paid' | translate }}</th>
                </tr>
              </thead>

              <tbody>
                @for (payment of invoice.payments; track payment.id) {
                  <tr>
                    <td class="av-source">{{ payment.stripe_payment_intent_id || unresolved }}</td>
                    <td>
                      {{ paymentStatusLabel(payment.stripe_status) | translate }}
                    </td>
                    <td class="text-end">
                      {{ payment.amount === null ? unresolved : (payment.amount | number: '1.2-2') }}
                      {{ payment.currency || '' }}
                    </td>
                    <td class="text-end">{{ payment.refunded_amount | number: '1.2-2' }}</td>
                    <td class="cell-muted">
                      {{ payment.failure_message || payment.failure_code || unresolved }}
                    </td>
                    <td class="cell-muted">
                      {{ payment.paid_at ? (payment.paid_at | date: dateTimeFormat) : unresolved }}
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </section>

      <section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="invoice-credit-notes">
        <h3 class="av-section-title m-0">{{ 'backoffice.billing.invoice_detail.credit_notes_title' | translate }}</h3>

        @if (invoice.credit_notes.length === 0) {
          <p class="av-meta m-0" data-testid="invoice-credit-notes-empty">{{ 'backoffice.billing.invoice_detail.credit_notes_empty' | translate }}</p>
        } @else {
          <div class="table-scroll">
            <table class="av-table w-100" data-testid="invoice-credit-note-table">
              <thead>
                <tr>
                  <th scope="col">{{ 'backoffice.billing.invoice_detail.credit_notes_table.credit_note' | translate }}</th>
                  <th scope="col">{{ 'backoffice.billing.invoice_detail.status' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.billing.invoice_detail.credit_notes_table.amount' | translate }}</th>
                  <th scope="col">{{ 'backoffice.billing.invoice_detail.credit_notes_table.reason' | translate }}</th>
                  <th scope="col">{{ 'backoffice.billing.invoice_detail.credit_notes_table.cash_refund' | translate }}</th>
                </tr>
              </thead>

              <tbody>
                @for (creditNote of invoice.credit_notes; track creditNote.id) {
                  <tr>
                    <td class="av-source">{{ creditNote.stripe_credit_note_id || unresolved }}</td>
                    <td>
                      {{ creditNoteStatusLabel(creditNote.status) | translate }}
                    </td>
                    <td class="text-end">
                      {{ creditNote.amount | number: '1.2-2' }} {{ creditNote.currency || '' }}
                    </td>
                    <td class="cell-muted">
                      {{ creditNoteReasonLabel(creditNote.reason) | translate }}
                    </td>
                    <td class="cell-muted">{{ (creditNote.refunded ? 'backoffice.common.yes' : 'backoffice.common.no') | translate }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </section>
    } @else {
      <av-empty-state
        class="mt-3"
        icon="description"
        [title]="'backoffice.billing.invoice_detail.empty_title' | translate"
        [message]="'backoffice.billing.invoice_detail.empty_message' | translate"
      ></av-empty-state>
    }
  }
  @default {
    <av-empty-state
      class="mt-3"
      icon="hourglass_empty"
      [title]="'backoffice.billing.invoice_detail.loading_title' | translate"
      [message]="'backoffice.billing.invoice_detail.loading_message' | translate"
    ></av-empty-state>
  }
}
